import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteShell from '@/components/feature/SiteShell';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  categoryMap,
  filterMovies,
  genresOf,
  inCategory,
  regionsOf,
  sortOptions,
  yearsOf,
  type SortKey,
} from '@/lib/catalog';
import FilterPanel from './components/FilterPanel';

const PAGE_SIZE = 8;

function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const category = type ? categoryMap[type as keyof typeof categoryMap] : undefined;
  const list = useMemo(() => (category ? inCategory(category.key) : []), [category]);

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('updated');
  const [page, setPage] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);

  usePageMeta({
    title: category ? `${category.label} - 黄牛视频影视内容平台` : '影视分类 - 黄牛视频影视内容平台',
    description: category
      ? `${category.description}及时获取最新影视资讯动态与推荐内容。`
      : '黄牛视频影视分类频道，提供电影、电视剧、综艺内容浏览与筛选。',
    keywords: category
      ? `黄牛视频,${category.label},${category.label}推荐,影视资讯,影片筛选`
      : '黄牛视频,影视分类,电影,电视剧,综艺',
  });

  useEffect(() => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setSort('updated');
    setPage(1);
    setSheetOpen(false);
  }, [type]);

  const genres = genresOf(list);
  const regions = regionsOf(list);
  const years = yearsOf(list);

  const filtered = useMemo(
    () =>
      filterMovies(list, {
        genres: selectedGenres,
        regions: selectedRegions,
        years: selectedYears,
        sort,
      }),
    [list, selectedGenres, selectedRegions, selectedYears, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const activeCount = selectedGenres.length + selectedRegions.length + selectedYears.length;

  const resetAll = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setPage(1);
  };

  if (!category) {
    return (
      <SiteShell>
        <div className="mx-auto w-full max-w-3xl px-4 py-24 text-center md:px-8">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">没有找到这个分类</h1>
          <p className="mt-3 text-sm text-foreground-500">
            分类地址可能已变更，你可以回到首页或直接进入电影频道。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-md bg-primary-900 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-800"
            >
              返回首页
            </Link>
            <Link
              to="/category/movie"
              className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
            >
              进入电影频道
            </Link>
          </div>
        </div>
      </SiteShell>
    );
  }

  const filterPanelProps = {
    genres,
    regions,
    years,
    selectedGenres,
    selectedRegions,
    selectedYears,
    onToggleGenre: (value: string) => {
      setSelectedGenres((prev) => toggleValue(prev, value));
      setPage(1);
    },
    onToggleRegion: (value: string) => {
      setSelectedRegions((prev) => toggleValue(prev, value));
      setPage(1);
    },
    onToggleYear: (value: string) => {
      setSelectedYears((prev) => toggleValue(prev, value));
      setPage(1);
    },
    onReset: resetAll,
    activeCount,
  };

  return (
    <SiteShell>
      <div className="w-full px-4 pb-2 pt-5 md:px-8 md:pt-7">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category.label }]} />
      </div>

      <section className="w-full px-4 pb-6 pt-3 md:px-8 md:pb-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-900 text-accent-400">
                <i className={`${category.icon} text-xl`} />
              </span>
              <div>
                <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
                  {category.label}频道
                </h1>
                <p className="mt-0.5 font-label text-[10px] uppercase tracking-[0.22em] text-foreground-400">
                  {category.enLabel} · HUANGNIU TV
                </p>
              </div>
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              {category.intro}
            </p>
          </div>
          <p className="text-xs text-foreground-500">
            共 <span className="font-label font-bold text-foreground-900">{filtered.length}</span> 部作品
            {activeCount > 0 && <span className="text-accent-700"> · 已应用 {activeCount} 个筛选条件</span>}
          </p>
        </div>
      </section>

      <section className="w-full px-4 pb-10 md:px-8 md:pb-14">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[248px_minmax(0,1fr)] lg:gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <FilterPanel {...filterPanelProps} />
              <div className="mt-4 rounded-lg bg-secondary-50 p-4">
                <p className="font-heading text-xs font-bold text-secondary-900">筛选说明</p>
                <p className="mt-2 text-[11px] leading-relaxed text-secondary-800/80">
                  同一组内可多选，不同组之间为“同时满足”。筛选与排序均为前端示例逻辑，结果会立即刷新。
                </p>
              </div>
            </div>
          </aside>

          <div className="min-w-0">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5">
              <div className="flex items-center gap-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setPage(1);
                    }}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                      sort === option.key
                        ? 'bg-primary-900 font-medium text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setSheetOpen(true)}
                className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line text-sm" />
                筛选
                {activeCount > 0 && (
                  <span className="rounded-full bg-accent-500 px-1.5 text-[10px] font-bold text-accent-950">
                    {activeCount}
                  </span>
                )}
              </button>
            </div>

            {paged.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
                  {paged.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>

                <div className="mt-8 flex items-center justify-center gap-1.5">
                  <button
                    type="button"
                    aria-label="上一页"
                    disabled={safePage === 1}
                    onClick={() => setPage(safePage - 1)}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      onClick={() => setPage(pageNumber)}
                      className={`h-8 min-w-8 cursor-pointer rounded-md px-2 font-label text-xs transition-colors ${
                        pageNumber === safePage
                          ? 'bg-primary-900 font-semibold text-background-50'
                          : 'border border-background-300 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                  <button
                    type="button"
                    aria-label="下一页"
                    disabled={safePage === totalPages}
                    onClick={() => setPage(safePage + 1)}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                  <span className="ml-2 text-[11px] text-foreground-400">
                    第 {safePage} / {totalPages} 页
                  </span>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-4 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-400">
                  <i className="ri-inbox-line" />
                </span>
                <h2 className="mt-4 font-heading text-base font-bold text-foreground-900">
                  没有符合条件的{category.label}
                </h2>
                <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-500">
                  试试减少筛选条件，或换一个年份看看。也可以直接重置全部条件回到完整列表。
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-900 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {sheetOpen && (
        <div className="fixed inset-0 z-50 flex items-end lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 cursor-pointer bg-primary-950/50"
          />
          <div className="relative z-10 max-h-[85vh] w-full animate-slide-in overflow-y-auto rounded-t-lg bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setSheetOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel {...filterPanelProps} />
            <div className="sticky bottom-0 mt-4 flex gap-2 bg-background-50 pt-2">
              <button
                type="button"
                onClick={resetAll}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm text-foreground-700"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex-[2] cursor-pointer whitespace-nowrap rounded-md bg-primary-900 py-2.5 text-sm font-medium text-background-50"
              >
                查看 {filtered.length} 部结果
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteShell>
  );
}